import { useState, useEffect } from 'react'
import { BookOpen, RefreshCw, Package, Phone, Hash, GraduationCap, LayoutList, Send, BarChart3, Bell, UserX, ChevronRight } from 'lucide-react'
import StatusSystemSection from '../components/Wiki/StatusSystemSection'
import ContactToolsSection from '../components/Wiki/ContactToolsSection'
import IntroductionSection from '../components/Wiki/IntroductionSection'
import NavigationSectionsGuide from '../components/Wiki/NavigationSectionsGuide'
import TalentPostingGuide from '../components/Wiki/TalentPostingGuide'
import StatisticsGuide from '../components/Wiki/StatisticsGuide'
import DiscordNotificationsGuide from '../components/Wiki/DiscordNotificationsGuide'
import OffboardingGuide from '../components/Wiki/OffboardingGuide'
import AnchorLink from '../components/Wiki/AnchorLink'

const sections = [
  {
    key: 'introduction',
    title: 'Introduction',
    icon: GraduationCap,
    description: 'How to add talents, change statuses, convert types, and work with job applications',
    component: IntroductionSection,
    badge: 'start here',
    subsections: [
      { id: 'intro-overview',       label: 'What is Talent Service?' },
      { id: 'intro-add-talent',     label: 'Adding a Candidate' },
      { id: 'intro-detail',         label: 'Talent Profile' },
      { id: 'intro-change-status',  label: 'Changing Status' },
      { id: 'intro-convert',        label: 'Converting Type' },
      { id: 'intro-job-apps',       label: 'Job Applications' },
      { id: 'intro-faq',            label: 'Common Questions' },
    ]
  },
  {
    key: 'navigation-sections',
    title: 'Sidebar Sections',
    icon: LayoutList,
    description: 'How each subsection in the sidebar is filtered — Candidates, Video, Interview, Employees, and more',
    component: NavigationSectionsGuide,
    badge: 'reference',
    subsections: [
      { id: 'nav-candidates',        label: 'Candidates' },
      { id: 'nav-employees',         label: 'Employees' },
      { id: 'nav-how-status-match',  label: 'Status filtering' },
      { id: 'nav-summary',           label: 'Summary table' },
      { id: 'nav-faq',              label: 'Common Questions' },
    ]
  },
  {
    key: 'statuses',
    title: 'Status System',
    icon: RefreshCw,
    description: 'How synchronization and talent status management works',
    component: StatusSystemSection,
    badge: 'core',
    subsections: [
      { id: 'statuses-intro',       label: 'What is it?' },
      { id: 'statuses-quickstart',  label: 'Quick Start' },
      { id: 'statuses-lists',       label: 'Status lists' },
      { id: 'statuses-faq',         label: 'Troubleshooting' },
      { id: 'statuses-next',        label: "What's Next" },
    ]
  },
  {
    key: 'contact-tools',
    title: 'Contact Tools & Types',
    icon: Phone,
    description: 'Which communication tools are available and how they are organized by type',
    component: ContactToolsSection,
    badge: 'reference',
    subsections: [
      { id: 'contact-tools-types',  label: 'Tool types' },
      { id: 'contact-tools-why',    label: 'Missing types?' },
      { id: 'contact-tools-guide',  label: 'Quick Guide' },
    ]
  },
  {
    key: 'statistics',
    title: 'Statistics',
    icon: BarChart3,
    description: 'How each metric is calculated — what events are tracked, who gets credited, and what the filters do',
    component: StatisticsGuide,
    badge: 'reference',
    subsections: [
      { id: 'stats-overview', label: 'Overview' },
      {
        group: 'Metrics',
        children: [
          { id: 'stats-talents-added', label: 'Talents Added' },
          { id: 'stats-videos',        label: 'Videos Added' },
          { id: 'stats-youtube',       label: 'YouTube Added' },
          { id: 'stats-portraits',     label: 'Portraits Added' },
          { id: 'stats-hired',         label: 'Hired' },
          { id: 'stats-started',       label: 'Started' },
          { id: 'stats-onboarding',    label: 'Onboarding Funnel' },
          { id: 'stats-custom',        label: 'Custom Reports' },
        ]
      },
      { id: 'stats-views',  label: 'View modes' },
      { id: 'stats-period', label: 'Period comparison' },
      { id: 'stats-faq',    label: 'Common Questions' },
    ]
  },
  {
    key: 'discord-notifications',
    title: 'Discord Notifications',
    icon: Bell,
    description: 'How automatic Discord messages work — event types, credential setup, and admin configuration',
    component: DiscordNotificationsGuide,
    badge: 'reference',
    subsections: [
      { id: 'discord-overview',          label: 'Overview' },
      { id: 'discord-how-it-works',      label: 'How it works' },
      {
        group: 'Event Types',
        children: [
          { id: 'discord-events-realtime',   label: 'Real-time events' },
          { id: 'discord-events-scheduled',  label: 'Scheduled events' },
        ]
      },
      { id: 'discord-credentials',  label: 'Credential setup' },
      { id: 'discord-admin-ui',     label: 'Admin UI' },
      { id: 'discord-env',          label: 'Environment variable' },
      { id: 'discord-faq',          label: 'Common Questions' },
    ]
  },
  {
    key: 'offboarding',
    title: 'Offboarding',
    icon: UserX,
    description: 'What happens when someone leaves — departure dates, status changes, posts, and the user account',
    component: OffboardingGuide,
    badge: 'guide',
    subsections: [
      { id: 'offboarding-overview',      label: 'How it works' },
      { id: 'offboarding-add-date',      label: 'Adding a date' },
      { id: 'offboarding-scheduled',     label: 'Dates set in advance' },
      { id: 'offboarding-edit-date',     label: 'Editing a date' },
      { id: 'offboarding-status-change', label: 'Changing status by hand' },
      { id: 'offboarding-effects',       label: 'What it sets off' },
      { id: 'offboarding-limits',        label: 'What it does not do' },
      { id: 'offboarding-faq',           label: 'Common Questions' },
    ]
  },
  {
    key: 'talent-posting',
    title: 'Talent Posting',
    icon: Send,
    description: 'How to create and publish talent posts — templates, AI generation, and the public catalog',
    component: TalentPostingGuide,
    badge: 'guide',
    subsections: [
      { id: 'posting-prompt',       label: 'Prompt Template' },
      { id: 'posting-template',     label: 'Talent Template' },
      { id: 'posting-create-post',  label: 'Create Post' },
      { id: 'posting-generate',     label: 'AI Generation' },
      { id: 'posting-publish',      label: 'Publishing' },
      { id: 'posting-duplicate',    label: 'Duplicate' },
      { id: 'posting-catalog',      label: 'Public Catalog' },
      { id: 'posting-faq',          label: 'Common Questions' },
    ]
  },
  {
    key: 'future',
    title: 'Other Sections',
    icon: Package,
    description: 'In development',
    component: null,
    badge: 'soon',
    subsections: []
  }
]

const activeSections = sections.filter(s => s.component)

// Flatten all trackable IDs (handles both flat items and grouped items)
const flattenSubsections = (subsections) =>
  (subsections || []).flatMap(sub =>
    sub.group ? sub.children : [sub]
  )

const allTrackableIds = [
  ...activeSections.map(s => s.key),
  ...activeSections.flatMap(s => flattenSubsections(s.subsections).map(sub => sub.id))
]

// Map any id → parent section key
const subsectionToSection = {}
activeSections.forEach(s => {
  flattenSubsections(s.subsections).forEach(sub => {
    subsectionToSection[sub.id] = s.key
  })
})

const scrollTo = (id) => {
  window.history.pushState(null, '', `#${id}`)
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
}

const Wiki = () => {
  const [activeId, setActiveId] = useState('introduction')

  // Derived: which section key is active
  const activeSection = subsectionToSection[activeId] ?? activeId

  useEffect(() => {
    const hash = window.location.hash?.slice(1)
    if (hash) {
      setTimeout(() => {
        document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth' })
      }, 100)
    }
  }, [])

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        // Pick the entry closest to the top of the viewport
        const visible = entries
          .filter(e => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
        if (visible.length > 0) {
          setActiveId(visible[0].target.id)
        }
      },
      { threshold: 0, rootMargin: '-80px 0px -55% 0px' }
    )

    allTrackableIds.forEach(id => {
      const el = document.getElementById(id)
      if (el) observer.observe(el)
    })
    return () => observer.disconnect()
  }, [])

  return (
    <div className="wiki-page bg-light-bg-primary dark:bg-dark-bg-primary min-h-screen">
      <title>Wiki | Talent Service</title>

      {/* ── HERO HEADER ── */}
      <div className="border-b border-light-border-primary dark:border-dark-border-primary pb-8">
        <div className="flex min-w-0 flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <p className="wiki-mono text-primary-500 uppercase tracking-widest text-xs font-medium mb-3">
              Beginner's Guide
            </p>
            <h1 className="wiki-heading text-3xl sm:text-4xl font-bold text-light-text-primary dark:text-dark-text-primary leading-tight">
              Wiki
            </h1>
            <p className="mt-2 text-base text-light-text-secondary dark:text-dark-text-secondary max-w-xl">
              Quick start documentation for the Talent Service project.
              Find everything you need to get up and running.
            </p>
          </div>
          <div className="mt-1 shrink-0 self-start">
            <span className="wiki-mono inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-light-bg-tertiary dark:bg-dark-bg-tertiary border border-light-border-primary dark:border-dark-border-primary text-xs text-light-text-tertiary dark:text-dark-text-tertiary">
              <Hash className="w-3 h-3" />
              {activeSections.length} articles
            </span>
          </div>
        </div>
      </div>

      {/* ── TWO-PANEL LAYOUT ── */}
      <div className="flex gap-0 relative mt-0">

        {/* ── LEFT SIDEBAR TOC ── */}
        <aside className="hidden lg:block w-60 shrink-0 self-start sticky top-6 pr-6 pt-8">
          <p className="wiki-mono text-xs uppercase tracking-widest text-light-text-tertiary dark:text-dark-text-tertiary mb-4 px-2">
            On this page
          </p>
          <nav className="space-y-0.5">
            {sections.map(section => {
              const Icon = section.icon
              const isSectionActive = activeSection === section.key
              const isDisabled = !section.component
              const hasSubs = section.subsections?.length > 0

              return (
                <div key={section.key}>
                  {/* Section row */}
                  <a
                    href={section.component ? `#${section.key}` : undefined}
                    onClick={e => {
                      if (!section.component) { e.preventDefault(); return }
                      e.preventDefault()
                      scrollTo(section.key)
                    }}
                    className={[
                      'group flex items-center gap-2.5 px-2 py-2 rounded-md text-sm transition-colors duration-150 cursor-pointer',
                      isSectionActive
                        ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 font-medium'
                        : isDisabled
                        ? 'text-light-text-tertiary dark:text-dark-text-tertiary cursor-default opacity-50'
                        : 'text-light-text-secondary dark:text-dark-text-secondary hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary hover:text-light-text-primary dark:hover:text-dark-text-primary'
                    ].join(' ')}
                  >
                    <span className={[
                      'flex-shrink-0 w-0.5 h-4 rounded-full transition-colors duration-200',
                      isSectionActive ? 'bg-primary-500' : 'bg-transparent'
                    ].join(' ')} />
                    <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                    <span className="truncate flex-1">{section.title}</span>
                    {section.badge === 'soon' && (
                      <span className="wiki-mono ml-auto text-[10px] px-1.5 py-0.5 rounded bg-light-bg-tertiary dark:bg-dark-bg-tertiary text-light-text-tertiary dark:text-dark-text-tertiary border border-light-border-primary dark:border-dark-border-primary">
                        soon
                      </span>
                    )}
                    {hasSubs && !isDisabled && (
                      <ChevronRight className={[
                        'w-3 h-3 flex-shrink-0 transition-transform duration-200',
                        isSectionActive ? 'rotate-90 text-primary-500' : 'text-light-text-tertiary dark:text-dark-text-tertiary'
                      ].join(' ')} />
                    )}
                  </a>

                  {/* Subsections — visible when section is active */}
                  {isSectionActive && hasSubs && (
                    <div className="ml-5 mt-0.5 mb-1 space-y-0.5 border-l border-light-border-primary dark:border-dark-border-primary pl-3">
                      {section.subsections.map((sub, i) => {
                        // Group item — label + indented children
                        if (sub.group) {
                          const isAnyChildActive = sub.children.some(c => activeId === c.id)
                          return (
                            <div key={sub.group}>
                              <div className="flex items-center gap-1.5 px-1.5 pt-2 pb-1">
                                <span className={[
                                  'flex-shrink-0 w-0.5 h-3 rounded-full transition-colors duration-200',
                                  isAnyChildActive ? 'bg-primary-500' : 'bg-transparent'
                                ].join(' ')} />
                                <p className={[
                                  'wiki-mono text-[10px] uppercase tracking-wider select-none transition-colors duration-200',
                                  isAnyChildActive
                                    ? 'text-primary-500 font-semibold'
                                    : 'text-light-text-tertiary dark:text-dark-text-tertiary'
                                ].join(' ')}>
                                  {sub.group}
                                </p>
                              </div>
                              <div className="ml-2 space-y-0.5 border-l border-light-border-primary dark:border-dark-border-primary pl-2">
                                {sub.children.map(child => {
                                  const isActive = activeId === child.id
                                  return (
                                    <a
                                      key={child.id}
                                      href={`#${child.id}`}
                                      onClick={e => { e.preventDefault(); scrollTo(child.id) }}
                                      className={[
                                        'flex items-center gap-1.5 py-0.5 px-1 rounded text-xs transition-colors duration-150 cursor-pointer',
                                        isActive
                                          ? 'text-primary-600 dark:text-primary-400 font-medium'
                                          : 'text-light-text-tertiary dark:text-dark-text-tertiary hover:text-light-text-primary dark:hover:text-dark-text-primary'
                                      ].join(' ')}
                                    >
                                      <span className={['w-1 h-1 rounded-full flex-shrink-0', isActive ? 'bg-primary-500' : 'bg-transparent'].join(' ')} />
                                      <span className="truncate">{child.label}</span>
                                    </a>
                                  )
                                })}
                              </div>
                            </div>
                          )
                        }

                        // Regular flat item
                        const isSubActive = activeId === sub.id
                        return (
                          <a
                            key={sub.id}
                            href={`#${sub.id}`}
                            onClick={e => { e.preventDefault(); scrollTo(sub.id) }}
                            className={[
                              'flex items-center gap-1.5 py-1 px-1.5 rounded text-xs transition-colors duration-150 cursor-pointer',
                              isSubActive
                                ? 'text-primary-600 dark:text-primary-400 font-medium'
                                : 'text-light-text-tertiary dark:text-dark-text-tertiary hover:text-light-text-primary dark:hover:text-dark-text-primary'
                            ].join(' ')}
                          >
                            <span className={['w-1 h-1 rounded-full flex-shrink-0', isSubActive ? 'bg-primary-500' : 'bg-transparent'].join(' ')} />
                            <span className="truncate">{sub.label}</span>
                          </a>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
          </nav>

          <hr className="my-6 border-light-border-primary dark:border-dark-border-primary" />

          <div className="px-2 space-y-1">
            <p className="wiki-mono text-[11px] text-light-text-tertiary dark:text-dark-text-tertiary">
              {activeSections.length} sections available
            </p>
            <p className="wiki-mono text-[11px] text-light-text-tertiary dark:text-dark-text-tertiary">
              Last updated Apr 2026
            </p>
          </div>
        </aside>

        {/* ── RIGHT CONTENT AREA ── */}
        <div className="flex-1 min-w-0 pt-8 lg:border-l border-light-border-primary dark:border-dark-border-primary lg:pl-8">

          {/* Mobile section pills */}
          <div className="-mx-1 mb-6 min-w-0 overflow-x-auto overscroll-x-contain px-1 pb-2 lg:hidden">
            <div className="flex w-max gap-2" role="navigation" aria-label="Wiki sections">
              {activeSections.map(s => {
                const Icon = s.icon
                return (
                  <button
                    key={s.key}
                    onClick={() => scrollTo(s.key)}
                    className="flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-light-border-primary px-3 py-1.5 text-xs text-light-text-secondary transition-colors hover:border-primary-400 hover:text-primary-500 dark:border-dark-border-primary dark:text-dark-text-secondary"
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {s.title}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Welcome callout */}
          <div className="mb-10 flex gap-3 p-4 rounded-lg border border-light-border-secondary dark:border-dark-border-secondary bg-light-bg-secondary dark:bg-dark-bg-secondary">
            <BookOpen className="w-5 h-5 text-primary-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">
                Welcome to the Talent Service Wiki
              </p>
              <p className="mt-0.5 text-sm text-light-text-secondary dark:text-dark-text-secondary">
                This page will help you quickly understand the main system components.
                Browse sections in the sidebar or scroll to read in order.
              </p>
            </div>
          </div>

          {/* ── ARTICLE SECTIONS ── */}
          <div className="space-y-16">
            {sections.map((section) => {
              const Icon = section.icon

              if (!section.component) {
                return (
                  <section key={section.key} id={section.key} className="scroll-mt-6">
                    <div className="border-2 border-dashed border-light-border-primary dark:border-dark-border-primary rounded-xl p-8 text-center">
                      <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-light-bg-tertiary dark:bg-dark-bg-tertiary mb-4">
                        <Package className="w-5 h-5 text-light-text-tertiary dark:text-dark-text-tertiary" />
                      </div>
                      <h2 className="wiki-heading text-lg font-semibold text-light-text-secondary dark:text-dark-text-secondary mb-2">
                        {section.title}
                      </h2>
                      <p className="text-sm text-light-text-tertiary dark:text-dark-text-tertiary mb-4">
                        This section is under development and will be available soon.
                      </p>
                      <span className="wiki-mono inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border border-light-border-primary dark:border-dark-border-primary text-light-text-tertiary dark:text-dark-text-tertiary">
                        Coming soon
                      </span>
                    </div>
                  </section>
                )
              }

              const SectionComponent = section.component
              const sectionIndex = activeSections.findIndex(s => s.key === section.key)

              return (
                <section key={section.key} id={section.key} className="scroll-mt-6">
                  {/* Article header */}
                  <div className="mb-8">
                    <div className="mb-3 flex flex-wrap items-center gap-3">
                      <span className="wiki-mono text-5xl font-bold text-light-border-primary dark:text-dark-bg-tertiary select-none leading-none">
                        0{sectionIndex + 1}
                      </span>
                      <span className="wiki-mono text-[10px] uppercase tracking-widest px-2 py-0.5 rounded bg-light-bg-tertiary dark:bg-dark-bg-tertiary border border-light-border-primary dark:border-dark-border-primary text-light-text-tertiary dark:text-dark-text-tertiary">
                        {section.badge}
                      </span>
                    </div>
                    <div className="group mb-1 flex min-w-0 items-start gap-2.5">
                      <Icon className="w-5 h-5 text-primary-500 flex-shrink-0" />
                      <h2 className="wiki-heading min-w-0 break-words text-2xl font-bold text-light-text-primary dark:text-dark-text-primary">
                        {section.title}
                      </h2>
                      <AnchorLink id={section.key} />
                    </div>
                    <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary ml-7">
                      {section.description}
                    </p>
                    <div className="mt-4 h-px bg-gradient-to-r from-primary-500/60 via-primary-500/20 to-transparent" />
                  </div>

                  <SectionComponent />

                  {sectionIndex < activeSections.length - 1 && (
                    <div className="mt-16 h-px bg-light-border-primary dark:bg-dark-border-primary" />
                  )}
                </section>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Wiki
